import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Wallet, RefreshCw, ExternalLink } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const usd = (n) => (n == null ? 'Unavailable' : `$${Number(n).toFixed(2)}`);
const short = (a) => (a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '');

function TreasuryCounter({ data, onRefresh, refreshing }) {
  const gate = data.logged_totals?.gate || { usdc: 0, count: 0 };
  const ai = data.logged_totals?.ai || { usdc: 0, count: 0 };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <motion.div key={data.treasury_usdc} initial={{ scale: 0.98, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }} className="md:col-span-1 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5" data-testid="fee-treasury-card">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-400 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Treasury balance, live</span>
          <button onClick={onRefresh} className="text-gray-500 hover:text-white transition-colors" data-testid="fee-refresh-btn" aria-label="Refresh"><RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /></button>
        </div>
        <div className="text-3xl font-bold text-white font-mono" data-testid="fee-treasury-balance">{usd(data.treasury_usdc)}</div>
        <div className="text-[11px] text-gray-500 mt-1 font-mono" data-testid="fee-treasury-address">USDC on {data.chain}, {short(data.pay_to)}</div>
      </motion.div>
      <div className="rounded-xl border border-white/5 bg-white/[0.03] p-5" data-testid="fee-gate-total">
        <div className="text-xs text-gray-500 mb-3">Binding Gate fees (logged)</div>
        <div className="text-2xl font-bold text-white font-mono">{usd(gate.usdc)}</div>
        <div className="text-[11px] text-gray-500 mt-1">{gate.count} executed {gate.count === 1 ? 'check' : 'checks'}</div>
      </div>
      <div className="rounded-xl border border-white/5 bg-white/[0.03] p-5" data-testid="fee-ai-total">
        <div className="text-xs text-gray-500 mb-3">Premium AI fees (logged)</div>
        <div className="text-2xl font-bold text-white font-mono">{usd(ai.usdc)}</div>
        <div className="text-[11px] text-gray-500 mt-1">{ai.count} paid {ai.count === 1 ? 'call' : 'calls'}</div>
      </div>
    </div>
  );
}

function DailyChart({ daily }) {
  const rows = daily.map((d) => ({ ...d, label: new Date(`${d.date}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) }));
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.03] p-5" data-testid="fee-daily-chart">
      <div className="text-xs text-gray-500 mb-4">Daily fees, last 7 days (UTC)</div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis dataKey="label" stroke="#6b7280" fontSize={11} />
          <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(v) => `$${v}`} />
          <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} contentStyle={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="gate" name="Binding Gate" stackId="f" fill="#34d399" />
          <Bar dataKey="ai" name="Premium AI" stackId="f" fill="#60a5fa" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RecentPayments({ recent }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.03] p-5" data-testid="fee-recent-list">
      <div className="text-xs text-gray-500 mb-3">Latest payments</div>
      {recent.length === 0 ? <p className="text-xs text-gray-600" data-testid="fee-recent-empty">No payments logged yet. The log started with this release; earlier payments show in the treasury balance only.</p> : (
        <div className="divide-y divide-white/5">
          {recent.map((p) => (
            <div key={p.tx_hash} className="flex items-center justify-between py-2 text-xs" data-testid={`fee-payment-${p.tx_hash.slice(0, 10)}`}>
              <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${p.kind === 'gate' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-blue-500/10 text-blue-300'}`}>{p.kind === 'gate' ? 'Gate' : 'AI'}</span>
              <span className="font-mono text-white">{usd(p.amount_usdc)}</span>
              <span className="font-mono text-gray-500 hidden sm:inline">{short(p.payer)}</span>
              <span className="text-gray-500 hidden md:inline">{new Date(p.created_at).toLocaleString('en-US', { month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              <a href={p.explorer_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid={`fee-tx-link-${p.tx_hash.slice(0, 10)}`}>Tx <ExternalLink className="w-3 h-3" /></a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FeeRevenuePanel() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${API}/api/admin/fees/summary`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setData(await r.json());
    } finally { setRefreshing(false); }
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  return (
    <div data-testid="fee-revenue-panel">
      <div className="flex items-center gap-2 mb-3">
        <Wallet className="w-4 h-4 text-emerald-400" />
        <h3 className="text-sm font-semibold text-gray-300">Fee Revenue (x402)</h3>
      </div>
      {!data ? <div className="text-xs text-gray-500" data-testid="fee-loading">Loading fee data...</div>
        : !data.enabled ? <div className="text-xs text-gray-500" data-testid="fee-disabled">x402 fees are not configured on this environment.</div>
        : (
          <div className="space-y-4">
            <TreasuryCounter data={data} onRefresh={load} refreshing={refreshing} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <DailyChart daily={data.daily} />
              <RecentPayments recent={data.recent} />
            </div>
          </div>
        )}
    </div>
  );
}
